import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Camera, X, Loader2, ImagePlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhotoUpload = ({ photos = [], onPhotosChange, maxPhotos = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    const remaining = maxPhotos - photos.length;
    const toUpload = files.slice(0, remaining);
    if (toUpload.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
    const newPhotos = [...photos];

    for (const file of toUpload) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/inspections/upload-photo`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        newPhotos.push({
          path: res.data.path,
          id: res.data.id,
          filename: res.data.filename
        });
      } catch (err) {
      }
    }

    onPhotosChange(newPhotos);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    const updated = photos.filter((_, i) => i !== index);
    onPhotosChange(updated);
  };

  return (
    <div data-testid="photo-upload-section">
      <div className="flex flex-wrap gap-2">
        {photos.map((photo, idx) => (
          <PhotoThumb key={photo.id || idx} photo={photo} onRemove={() => removePhoto(idx)} />
        ))}
        
        {photos.length < maxPhotos && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 hover:border-amber-400 flex flex-col items-center justify-center text-stone-400 hover:text-amber-600 transition-colors"
            data-testid="add-photo-btn"
          >
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <Camera className="h-5 w-5" />
                <span className="text-[10px] mt-0.5">Add</span>
              </>
            )}
          </button>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="photo-file-input"
      />
      
      {photos.length > 0 && (
        <p className="text-xs text-stone-400 mt-1">{photos.length}/{maxPhotos} photos</p>
      )}
    </div>
  );
};

const PhotoThumb = ({ photo, onRemove }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    let cancelled = false;
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('crewToken');
        const res = await axios.get(`${API}/files/${photo.path}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        if (!cancelled) {
          const url = URL.createObjectURL(res.data);
          setBlobUrl(url);
        }
      } catch {
        // Failed to load
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (photo.path) fetchImage();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [photo.path]);

  return (
    <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-stone-200 bg-stone-100">
      {loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
        </div>
      ) : blobUrl ? (
        <img src={blobUrl} alt={photo.filename || 'Photo'} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImagePlus className="h-4 w-4 text-stone-400" />
        </div>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
          data-testid="remove-photo-btn"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

export { PhotoUpload, PhotoThumb };
